const ADMIN_ROLE_IDS = ['1', '2', '5'];
const COUNSELOR_ROLE_ID = '4';
const ALL_ROLE_IDS = ['1', '2', '3', '4', '5'];

const normalizePathPart = (value) => {
	if(value === undefined || value === null || value === '' || value === 'null') return null;
	return value;
};

const getRoleID = (userInfo) => (userInfo && userInfo.role_id !== undefined && userInfo.role_id !== null) ? `${userInfo.role_id}` : null;

const hasPermission = (allowed_roles = [], role_id) => allowed_roles.includes(role_id);

const normalizeProgressSubscreen = (screen, subscreen) => {
	if(screen === 'section_creation') return 'create_sections';
	if(screen === 'manage_departments' && (!subscreen || subscreen === 'manage_departments')) return 'add_supervisors';
	if(screen === 'place_sections' && subscreen === 'placement_permissions') return 'manual_placements';
	return subscreen;
};

const hasProgressPermission = ({ role_id, schoolType, rolePages, screen, subscreen }) => {
	if(role_id === COUNSELOR_ROLE_ID) return false;
	if(schoolType !== 'hsms') return hasPermission(ADMIN_ROLE_IDS, role_id);
	if(!screen) return true;
	if(!rolePages) return true;
	if(!rolePages[screen]) return false;
	if(!subscreen) return true;

	const normalized_subscreen = normalizeProgressSubscreen(screen, subscreen);
	const role_page_subscreens = Array.isArray(rolePages[screen].subscreens) ? rolePages[screen].subscreens : [];
	return role_page_subscreens.some(subscreen_info => subscreen_info.subscreen === normalized_subscreen);
};

const schoolDataPermissions = {
	hsms: {
		courses: ['1', '2', '3', '5'],
		teachers: ['1', '2', '3', '5'],
		classrooms: ['1', '2', '3', '5'],
		students: ['1', '2', '4', '5'],
	},
	elementary: {
		courses: ALL_ROLE_IDS,
		teachers: ALL_ROLE_IDS,
		classrooms: ALL_ROLE_IDS,
		students: ALL_ROLE_IDS,
		blocks: ALL_ROLE_IDS,
		pullout_groups: ALL_ROLE_IDS,
	},
};

const schedulingDataPermissions = {
	requests: ALL_ROLE_IDS,
	sections: ALL_ROLE_IDS,
	labels: ADMIN_ROLE_IDS,
};

const documentationPermissions = {
	getting_started: { permissions: ALL_ROLE_IDS, subscreens: {} },
	scheduling_wizard: {
		permissions: ['1', '2', '3', '5'],
		subscreens: {
			schedule_structure: ['1', '2', '5'],
			upload_data: ['1', '2', '5'],
			manage_departments: ['1', '2', '5'],
			edit_constraints: ['1', '2', '3', '5'],
			create_sections: ['1', '2', '3', '5'],
			place_sections: ['1', '2', '3', '5'],
			place_students: ['1', '2', '5'],
		},
	},
	school_data: {
		permissions: ALL_ROLE_IDS,
		subscreens: {
			courses: ['1', '2', '3', '5'],
			teachers: ['1', '2', '3', '5'],
			classrooms: ['1', '2', '3', '5'],
			students: ['1', '2', '4', '5'],
		},
	},
	scheduling_data: {
		permissions: ALL_ROLE_IDS,
		subscreens: {
			requests: ALL_ROLE_IDS,
			sections: ALL_ROLE_IDS,
		},
	},
	magnetboard: { permissions: ALL_ROLE_IDS, subscreens: {} },
	report_a_bug: { permissions: ALL_ROLE_IDS, subscreens: {} },
};

const hasDocumentationPermission = ({ role_id, screen, subscreen }) => {
	const screen_key = screen || 'getting_started';
	const screen_info = documentationPermissions[screen_key];
	if(!screen_info || !hasPermission(screen_info.permissions, role_id)) return false;
	if(!subscreen) return true;
	if(Object.keys(screen_info.subscreens).length === 0) return true;

	const subscreen_permissions = screen_info.subscreens[subscreen];
	if(!subscreen_permissions) return false;
	return hasPermission(subscreen_permissions, role_id);
};

export const getDefaultRouteForRole = (userInfo, schoolType) => {
	const role_id = getRoleID(userInfo);
	const route_school_type = schoolType || 'hsms';
	const default_screen = role_id === COUNSELOR_ROLE_ID ? 'magnetboard' : 'progress';

	return `/app/${route_school_type}/${default_screen}`;
};

export const userCanAccessRoute = ({ userInfo, schoolType, routeType, params = {}, rolePages = null }) => {
	const role_id = getRoleID(userInfo);
	if(!role_id) return true;

	const route_school_type = normalizePathPart(params.school_type);
	const effective_school_type = schoolType || route_school_type;
	if(route_school_type && schoolType && route_school_type !== schoolType) return false;

	const screen = normalizePathPart(params.screen);
	const subscreen = normalizePathPart(params.subscreen);
	const page = normalizePathPart(params.page);

	switch(routeType) {
		case 'settings':
			if(!page || page === 'account') return true;
			if(page === 'users') return hasPermission(['1', '2'], role_id);
			return false;
		case 'documentation':
			return hasDocumentationPermission({role_id, screen, subscreen});
		case 'progress':
			return hasProgressPermission({role_id, schoolType: effective_school_type, rolePages, screen, subscreen});
		case 'school_data': {
			if(!screen) return true;
			const permissions = schoolDataPermissions[effective_school_type] || {};
			return hasPermission(permissions[screen] || [], role_id);
		}
		case 'scheduling_data':
			if(effective_school_type !== 'hsms') return false;
			if(!screen) return true;
			return hasPermission(schedulingDataPermissions[screen] || [], role_id);
		case 'magnetboard':
			return true;
		default:
			return true;
	}
};

export const navigationGuardIsPending = ({ authComplete, isAuth, userInfo, userInfoLoading, schoolType, schoolInfoLoading, routeType, params = {}, rolePages = null }) => {
	if(!authComplete) return true;
	if(!isAuth) return false;
	if(userInfoLoading || schoolInfoLoading || !userInfo || !userInfo.role_id || !schoolType) return true;

	const role_id = getRoleID(userInfo);
	const screen = normalizePathPart(params.screen);
	if(routeType === 'progress' && role_id !== COUNSELOR_ROLE_ID && schoolType === 'hsms' && screen && !rolePages) return true;

	return false;
};

export const getNavigationGuardRedirect = ({ authComplete, isAuth, userInfo, userInfoLoading, schoolType, schoolInfoLoading, routeType, params = {}, rolePages = null, currentPathname = '' }) => {
	if(navigationGuardIsPending({authComplete, isAuth, userInfo, userInfoLoading, schoolType, schoolInfoLoading, routeType, params, rolePages})) return null;
	if(!isAuth) return null;
	if(userCanAccessRoute({userInfo, schoolType, routeType, params, rolePages})) return null;

	const redirect_path = getDefaultRouteForRole(userInfo, schoolType);
	return currentPathname === redirect_path ? null : redirect_path;
};
